"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleAlert as AlertCircle, ArrowRight, CircleCheck as CheckCircle2, Clock, Droplets, Flame, Gauge, Layers, PhoneCall, Pipette, RefreshCw, ShieldAlert, Sparkles, Wrench, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const servicesList = [{
  id: "drain-cleaning",
  title: "Drain Cleaning",
  badge: "Same-Day Clearing",
  description: "Targeted clog clearing for sinks, tubs, and main lines using professional motorized snakes and hydro diagnostic gear.",
  iconName: "Pipette",
  link: "/drain-cleaning/",
  actionText: "Clear Blocked Drain"
}, {
  id: "water-heater-repair",
  title: "Water Heater Repair",
  badge: "Diagnostic & Fix",
  description: "Prompt troubleshooting and repair for pilot issues, faulty thermostats, burnt heating elements, and pressure valves.",
  iconName: "Flame",
  link: "/water-heater-repair/",
  actionText: "Fix Water Heater"
}, {
  id: "water-heater-replacement",
  title: "Water Heater Replacement",
  badge: "Energy Efficient",
  description: "Complete removal and new installation of high-efficiency gas and electric tank systems backed by warranty.",
  iconName: "RefreshCw",
  link: "/water-heater-replacement/",
  actionText: "Replace Water Heater"
}, {
  id: "leak-detection-repair",
  title: "Leak Detection & Repair",
  badge: "Non-Invasive",
  description: "Precision locating and immediate repair for hidden wall, ceiling, slab, and pressurized water line leaks.",
  iconName: "Gauge",
  link: "/leak-detection-repair/",
  actionText: "Locate & Fix Leak"
}, {
  id: "toilet-repair",
  title: "Toilet Repair",
  badge: "Rapid Response",
  description: "Fixing persistent running, stubborn backups, broken flush valves, seal failures, and base leaks quickly.",
  iconName: "AlertCircle",
  link: "/toilet-repair/",
  actionText: "Repair Toilet"
}, {
  id: "faucet-sink-repair",
  title: "Faucet & Sink Repair",
  badge: "Precision Work",
  description: "Stopping stubborn drips, worn cartridge replacements, sprayer line repairs, and restoring kitchen and bath seals.",
  iconName: "Droplets",
  link: "/faucet-sink-repair/",
  actionText: "Fix Faucet or Sink"
}, {
  id: "sewer-line-repair",
  title: "Sewer Line Repair",
  badge: "Root & Line Fix",
  description: "In-depth camera inspection and targeted repairs for tree root intrusions, collapsed pipes, and severe backups.",
  iconName: "ShieldAlert",
  link: "/sewer-line-repair/",
  actionText: "Inspect Sewer Line"
}, {
  id: "pipe-repair",
  title: "Pipe Repair",
  badge: "Emergency Patch",
  description: "Fast isolation, durable section replacement, and leak stopping for burst, frozen, or corroded supply lines.",
  iconName: "Wrench",
  link: "/pipe-repair/",
  actionText: "Repair Damaged Pipe"
}, {
  id: "repiping",
  title: "Repiping",
  badge: "Whole-Home Upgrade",
  description: "Comprehensive pipe replacement for aging galvanized, defective polybutylene, or deteriorating copper plumbing systems.",
  iconName: "Layers",
  link: "/repiping/",
  actionText: "Explore Repiping"
}, {
  id: "garbage-disposal-repair",
  title: "Garbage Disposal Repair",
  badge: "Jam & Motor Fix",
  description: "Unjamming stuck impellers, resolving electrical hums, replacing failing motors, and fixing under-sink drainage leaks.",
  iconName: "Sparkles",
  link: "/garbage-disposal-repair/",
  actionText: "Fix Garbage Disposal"
}, {
  id: "tankless-water-heater-service",
  title: "Tankless Water Heater Service",
  badge: "On-Demand Units",
  description: "Annual descaling flushes, sensor calibration, ignition diagnostics, and high-efficiency tankless installations.",
  iconName: "Zap",
  link: "/tankless-water-heater-service/",
  actionText: "Service Tankless Unit"
}];
const iconMap: Record<string, React.ElementType> = {
  Pipette,
  Flame,
  RefreshCw,
  Gauge,
  AlertCircle,
  Droplets,
  ShieldAlert,
  Wrench,
  Layers,
  Sparkles,
  Zap
};
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="our-plumbing-services" data-nav="light" className="bg-background py-14 text-foreground md:py-20 lg:py-24">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-secondary-foreground">
            <Clock className="size-3.5 text-primary" />
            <span>24/7 Rapid Local Response</span>
          </div>

          <h2 className="mt-4 font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Browse Our Plumbing Services
          </h2>

          <p className="mt-3 font-sans text-base text-muted-foreground sm:text-lg">
            Direct, reliable plumbing solutions tailored for homeowners in Jonesboro, GA. 
            From emergency repairs to precision fixture and water heater service, our licensed technicians are ready to assist.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.05
      }} className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {servicesList.map((service, i) => {
          const IconComponent = iconMap[service.iconName] || Wrench;
          return <motion.div id={service.id} key={service.id} data-index={i} variants={itemVariants} className="flex h-full flex-col scroll-mt-24">
                <Card className="group relative flex h-full flex-col justify-between rounded-2xl border border-border bg-card p-6 shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                  <div>
                    {/* Top Accent Stripe Motif */}
                    <div className="h-1 w-10 rounded-full bg-primary" />

                    {/* Card Header & Icon */}
                    <CardHeader className="p-0 pt-5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex size-12 items-center justify-center rounded-xl bg-secondary text-primary transition-[background-color] group-hover:bg-primary group-hover:text-primary-foreground">
                          <IconComponent className="size-6" />
                        </div>
                        <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                          {service.badge}
                        </span>
                      </div>

                      <CardTitle className="mt-4 font-default text-xl font-bold text-card-foreground">
                        {service.title}
                      </CardTitle>
                    </CardHeader>

                    {/* Card Description */}
                    <CardContent className="p-0 pt-3">
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {service.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  {/* Card Footer Link */}
                  <CardFooter className="p-0 pt-6">
                    <Button variant="outline" asChild className="w-full justify-between rounded-full border-border text-xs font-semibold text-foreground hover:bg-secondary hover:text-secondary-foreground sm:text-sm">
                      <Link to={service.link}>
                        <span>{service.actionText}</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Immediate Call-to-Action Bar */}
        <div className="mt-12 rounded-2xl border border-border bg-card p-6 shadow-md sm:p-8 md:mt-16">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="space-y-1.5 text-center md:text-left">
              <div className="flex items-center justify-center gap-2 md:justify-start">
                <CheckCircle2 className="size-5 text-accent" />
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Need Help Right Now?
                </span>
              </div>
              <h3 className="font-default text-xl font-bold text-foreground sm:text-2xl">
                Have an urgent leak or backup in Jonesboro?
              </h3>
              <p className="text-sm text-muted-foreground">
                Speak directly with an on-call emergency plumber in seconds. No waiting queues.
              </p>
            </div>

            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <a href="tel:4708479076" className="w-full sm:w-auto">
                <Button className="w-full gap-2 rounded-full bg-accent px-6 py-6 text-base font-bold text-accent-foreground shadow-sm hover:brightness-105 active:scale-98">
                  <PhoneCall className="size-5" />
                  <span>Call (470) 847-9076</span>
                </Button>
              </a>
              <Button variant="outline" asChild className="w-full rounded-full border-border px-6 py-6 text-base font-semibold text-foreground hover:bg-secondary sm:w-auto">
                <Link to="/contact/">
                  <span>Online Request</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}